import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { CloseIcon } from './Icons';
import { TEMPLATE_VARIABLES, fullTimestamp } from './emailUtils';

const EMPTY_FORM = { id: null, name: '', shortcut: '', subject: '', body: '', active: true };

function QuickRepliesTab({ isAdmin }) {
  const { authFetch } = useAuth();
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`/api/email/quick-replies${isAdmin ? '?all=1' : ''}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not load quick replies');
      setReplies(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const changed = () => window.dispatchEvent(new Event('email-quick-replies-changed'));

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        shortcut: form.shortcut,
        subject: form.subject,
        body: form.body,
        active: form.active,
      };
      const response = await authFetch(form.id ? `/api/email/quick-replies/${form.id}` : '/api/email/quick-replies', {
        method: form.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not save');
      setForm(null);
      changed();
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (reply) => {
    const response = await authFetch(`/api/email/quick-replies/${reply.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !reply.active }),
    });
    if (response.ok) {
      changed();
      load();
    }
  };

  const remove = async (reply) => {
    if (!window.confirm(`Delete the quick reply "${reply.name}"? This can't be undone.`)) return;
    const response = await authFetch(`/api/email/quick-replies/${reply.id}`, { method: 'DELETE' });
    if (response.ok) {
      changed();
      load();
    }
  };

  if (form) {
    return (
      <form className="ew-settings-form" onSubmit={save}>
        <label>
          <span>Name</span>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={80} required autoFocus />
        </label>
        <div className="ew-settings-row">
          <label>
            <span>Shortcut (optional)</span>
            <input
              value={form.shortcut}
              onChange={(e) => setForm({ ...form, shortcut: e.target.value })}
              placeholder="e.g. track"
              maxLength={30}
            />
          </label>
          <label>
            <span>Subject for a new email (optional)</span>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} maxLength={200} />
          </label>
        </div>
        <label>
          <span>Message</span>
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={9} required />
        </label>
        <p className="ew-muted ew-settings-help">
          Filled in automatically when known:{' '}
          {TEMPLATE_VARIABLES.map((item) => (
            <code key={item.key}>{`{{${item.key}}}`}</code>
          ))}
          . Anything the CRM doesn't know shows as e.g. [order number] for the agent to fill in. Use [brackets] for
          anything an agent must always type, such as [tracking link].
        </p>
        <label className="ew-check">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          Active (shown to agents)
        </label>
        {error && <div className="ew-field-error">{error}</div>}
        <div className="ew-settings-actions">
          <button type="button" className="btn btn-secondary btn-small" onClick={() => setForm(null)} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary btn-small" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="ew-settings-list">
      {!isAdmin && <p className="ew-muted">Only a superadmin can add or change quick replies.</p>}
      {isAdmin && (
        <button type="button" className="btn btn-primary btn-small" onClick={() => setForm({ ...EMPTY_FORM })}>
          Add quick reply
        </button>
      )}
      {error && <div className="ew-field-error">{error}</div>}
      {loading ? (
        <p className="ew-muted">Loading…</p>
      ) : replies.length === 0 ? (
        <p className="ew-muted">No quick replies yet.</p>
      ) : (
        <ul className="ew-settings-items">
          {replies.map((reply) => (
            <li key={reply.id} className={reply.active ? '' : 'ew-settings-item-off'}>
              <div className="ew-settings-item-main">
                <strong>{reply.name}</strong>
                {reply.shortcut && <span className="ew-quick-shortcut">/{reply.shortcut}</span>}
                {!reply.active && <span className="ew-tag">Disabled</span>}
                <span className="ew-settings-item-body">{reply.body.replace(/\s+/g, ' ').slice(0, 140)}</span>
              </div>
              {isAdmin && (
                <div className="ew-settings-item-actions">
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() =>
                      setForm({
                        id: reply.id,
                        name: reply.name,
                        shortcut: reply.shortcut || '',
                        subject: reply.subject || '',
                        body: reply.body,
                        active: reply.active,
                      })
                    }
                  >
                    Edit
                  </button>
                  <button type="button" className="link-btn" onClick={() => toggleActive(reply)}>
                    {reply.active ? 'Disable' : 'Enable'}
                  </button>
                  <button type="button" className="link-btn ew-danger-link" onClick={() => remove(reply)}>
                    Delete
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SignatureTab({ signature, onSaved }) {
  const { authFetch } = useAuth();
  const [value, setValue] = useState(signature || '');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const save = async (event) => {
    event.preventDefault();
    setStatus('saving');
    setError('');
    try {
      const response = await authFetch('/api/email/me/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature: value }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not save');
      onSaved?.(data.signature || '');
      setStatus('saved');
    } catch (err) {
      setError(err.message);
      setStatus('idle');
    }
  };

  return (
    <form className="ew-settings-form" onSubmit={save}>
      <label>
        <span>Your signature</span>
        <textarea
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setStatus('idle');
          }}
          rows={5}
          maxLength={2000}
          placeholder={'Regards,\nRajarani Product Support Team'}
        />
      </label>
      <p className="ew-muted ew-settings-help">
        Added once at the bottom of every new reply, new email and forward you write. You can edit or delete it in the
        message before sending. Only you see this signature.
      </p>
      {error && <div className="ew-field-error">{error}</div>}
      <div className="ew-settings-actions">
        {status === 'saved' && <span className="ew-muted">Saved</span>}
        <button type="submit" className="btn btn-primary btn-small" disabled={status === 'saving'}>
          {status === 'saving' ? 'Saving…' : 'Save signature'}
        </button>
      </div>
    </form>
  );
}

function StatusTab() {
  const { authFetch } = useAuth();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    authFetch('/api/email/status')
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => (ok ? setStatus(data) : setError(data.error || 'Could not load status')))
      .catch((err) => setError(err.message));
  }, [authFetch]);
  if (error) return <div className="ew-field-error">{error}</div>;
  if (!status) return <p className="ew-muted">Loading…</p>;
  const sync = status.sentSync || {};
  const rows = [
    ['Hostinger API key and mailbox', status.mailConfigured ? 'Configured' : 'Missing: email can’t be sent or browsed'],
    [
      'Webhook secret',
      status.webhookSecretConfigured
        ? 'Set: deliveries without it are rejected'
        : status.webhookSecretRequired
        ? 'Required but not set: every delivery is rejected'
        : 'Not set: the webhook accepts unauthenticated deliveries',
    ],
    [
      'Sent-folder sync',
      sync.enabled
        ? `${sync.lastError ? `Last run failed: ${sync.lastError}` : 'Working'}${sync.lastRunAt ? ` · last run ${fullTimestamp(sync.lastRunAt)}` : ''}`
        : 'Off',
    ],
    ['CRM sends matched to Sent copies', String(sync.matched ?? 0)],
    ['Webmail replies brought in', String(sync.inserted ?? 0)],
    [
      'Reply threading (In-Reply-To)',
      status.threadingCheck
        ? status.threadingCheck.working
          ? `Confirmed working (${fullTimestamp(status.threadingCheck.checkedAt)})`
          : `Hostinger dropped it on the last check (${fullTimestamp(status.threadingCheck.checkedAt)})`
        : 'Not checked yet: confirmed after the first CRM reply reaches the Sent folder',
    ],
  ];
  return (
    <dl className="ew-status-list">
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function EmailSettings({ onClose, signature, onSignatureSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'superadmin';
  const [tab, setTab] = useState('quick');
  const tabs = [
    { key: 'quick', label: 'Quick replies' },
    { key: 'signature', label: 'My signature' },
    ...(isAdmin ? [{ key: 'status', label: 'Status' }] : []),
  ];
  return (
    <div className="modal-overlay modal-overlay-center" onClick={onClose}>
      <div className="modal-panel ew-settings" onClick={(event) => event.stopPropagation()} role="dialog" aria-label="Email settings">
        <div className="modal-header">
          <h2>Email settings</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>
        <div className="ew-settings-tabs" role="tablist">
          {tabs.map((item) => (
            <button
              type="button"
              key={item.key}
              role="tab"
              aria-selected={tab === item.key}
              className={tab === item.key ? 'ew-settings-tab-active' : ''}
              onClick={() => setTab(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="ew-settings-body">
          {tab === 'quick' && <QuickRepliesTab isAdmin={isAdmin} />}
          {tab === 'signature' && <SignatureTab signature={signature} onSaved={onSignatureSaved} />}
          {tab === 'status' && <StatusTab />}
        </div>
      </div>
    </div>
  );
}

export default EmailSettings;
